import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ChartRange } from '../components/detail/RangeToggle';

export interface SensorPoint {
  ts: string;
  value: number | null;
}

/** Duration in ms for each named range preset. */
export const RANGE_DURATION_MS: Record<ChartRange, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

export const sensorPointsKeys = {
  all: ['sensor-points'] as const,
  window: (id: string, start: string, end: string) =>
    [...sensorPointsKeys.all, id, start, end] as const,
};

export function useSensorPoints(
  id: string | null,
  start: string,
  end: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: sensorPointsKeys.window(id ?? '', start, end),
    queryFn: async (): Promise<SensorPoint[]> => {
      if (!id) return [];
      const result = await api.get<{ points: [string, number | null][] }>(
        `/v3/sensors/${id}/points`,
        { start, end },
      );
      return (result.points ?? []).map(([ts, value]) => ({ ts, value }));
    },
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
